import React from "react";
import { Grid, Theme } from "@mui/material";
import { createStyles, makeStyles } from "@material-ui/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import imgDefault from "../../../images/blog_home.webp";
import { useTranslation } from "react-i18next";
import { IArticleBlog } from "../../../utils/ArticleInterface";
import { createMarkup } from "../../../utils/App";
import { serverConfig } from "../../../utils/config";
import { useHistory } from "react-router";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    blog__card: {
      background: "transparent !important",
      border: "none !important",
      boxShadow: "none !important",
      display: 'block',
      flexDirection: (props: { layout: 'column' | 'row' }) => props.layout,
      width: '100%',
      padding: "24px 12px 12px 24px",
      margin: 'auto',
    },
    noPaddingLeft: {
      paddingLeft: "0 !important",
    },
    blog__grid: {
      [theme.breakpoints.down(1200)]: {
        display: "flex",
        justifyContent: "center",
      },
    },
    blog__cardContent: {
      minHeight: 100,
    },
    blog__btn: {
      fontSize: 14,
      fontFamily: "Josefin Sans",
      color: "#FFFFFF",
      textTransform: "capitalize",
      textAlign: "center",
      padding: "8px 14px",
      borderRadius: 100,
      background: "#890608",
      "&:hover": {
        background: "#B5551B",
      },
      [theme.breakpoints.up("sm")]: {
        minWidth: 180,
      },
    },
    clickable: {
      cursor: "pointer",
    },
    topDiv: {
      margin: 0, // Removes any default margin
      paddingBottom: 5, // Consistent spacing between `tagName` and `article.title`
      width: '100%', // Ensure full width
      [theme.breakpoints.down('sm')]: {
        fontSize: 16,
        margin: '10px 0px 5px 0px', // Adds bottom margin for spacing
      },
      [theme.breakpoints.up('md')]: {
        fontSize: 20,
        margin: '22px 0px 5px 0px', // Adds bottom margin for spacing
      },
    },
    secondDiv: {
      fontFamily: 'Roboto',
      margin: 0,
      fontWeight: 'bold',
      width: '100%', // Ensure full width
      [theme.breakpoints.down('sm')]: {
        fontSize: 16,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: 24,
      },
    },
    thirdDiv: {
      fontFamily: 'Roboto',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      width: '100%', // Ensure full width
      [theme.breakpoints.down('sm')]: {
        fontSize: 14,
        marginBottom: 12,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: 14,
        marginBottom: 18,
      },
    },
    cardMedia: {
      [theme.breakpoints.down('sm')]: {
        height: '100px',
        width: '150px',
        objectFit: 'fill',
      },
      [theme.breakpoints.up('md')]: {
        height: '188px',
        width: '313px',
        objectFit: 'fill',
      },
    },
    
    cardContent: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: 0,  // Removes extra padding for consistent height control
      minHeight: 50, // Adjusts to 200px height for all sizes
      maxHeight: 200, // Ensures the height is fixed to 200px
      [theme.breakpoints.down('sm')]: {
        marginLeft: '10px',
      },
      [theme.breakpoints.up('md')]: {
        marginLeft: '15px',
      },
    },
  })
);

const Env = serverConfig;

interface ArticleProps {
  article: IArticleBlog;
  showIntroduction?: boolean; // Prop to control the visibility of the introduction
  layout?: 'column' | 'row'; // Prop to control the layout
}
const Article = (props: ArticleProps) => {
  const { showIntroduction = true, layout = 'column' } = props; // Default values for the new props
  const classes = useStyles({ layout });
  const history = useHistory();
  const {i18n } = useTranslation<'common'>();
  const themeName = props.article.theme && props.article.theme.name
   ? i18n.language === 'fr'
     ? props.article.theme.name.fr
     : props.article.theme.name.en
   : ''; // Fallback if not populated


   // Helper function to get the image URL
  const getImageUrl = () => {
    if (!props.article.photo) {
      return imgDefault;
    }
    
    // New structure where photo is an object with filename
    if (typeof props.article.photo === 'object' && props.article.photo.filename) {
      return `${Env.ApiRoot}/images/articles/${props.article.photo.filename}`;
    }
    
    // Legacy structure where photo is a string
    if (typeof props.article.photo === 'string') {
      return `${Env.ApiRoot}/images/articles/${props.article.photo}`;
    }
    
    return imgDefault;
  };

  //console.log("Fetched Theme Name:", themeName);
  //console.log("Fetched Category Name:", categoryName);


  return (
    <Grid item xs={12} md={12} sm={12} lg={12} className={classes.blog__grid}>
      <Card className={classes.blog__card}>
        <CardMedia
            component="img"
            height="280"
            image={getImageUrl()}
            alt={props.article.photoAltText}
            sx={{ borderRadius: '12px' }}
        />
        <CardContent className={`${classes.noPaddingLeft} ${classes.cardContent}`}>
          <div onClick={() => history.push(`/blog/${props.article.slug}`)}>  
            <Typography
              gutterBottom
              color={"#890608"}
              fontFamily={"Roboto"}
              className={`${classes.topDiv} ${classes.clickable}`}
            >
              {themeName}
            </Typography>
          </div>
          <div 
            className={`${classes.secondDiv} ${classes.clickable}`}
            onClick={() => history.push(`/blog/${props.article.slug}`)}
          >  
            <Typography
              gutterBottom
              color="text.secondary"
              className={classes.secondDiv}
              sx={{ fontWeight: 'bold' }}
            >
              {props.article.title}
            </Typography>
          </div>
          {showIntroduction && (
            <div
              className={classes.clickable}
              onClick={() => history.push(`/blog/${props.article.slug}`)}
            >
              <Typography
                gutterBottom
                color={"#7d7d7d"}
                className={classes.thirdDiv}
              >
                <div
                  className={classes.blog__cardContent}
                  dangerouslySetInnerHTML={createMarkup(
                    `${props.article.introduction.substring(0, 150)}...`
                  )}
                ></div>
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default Article;
